import { AloftArea, AloftDate } from '@aloft/shared-objects';
import {
  addressToString,
  expected,
  getAbbreviatedCarportGarage,
  isDefined,
  orUndefined,
} from '@aloft/toolkit-shared';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
import { SalesComparisonGridColumnData } from '../types.ts';
import {
  formatAdjustmentPrice,
  formatFeatureArea,
  formatFeatureAreas,
  getBathCountAdjustment,
  getDistanceToSubject,
  getFoundationDisplay,
  getPropertyImageWithFallback,
} from './shared.tsx';

export async function convertSalesComparisonDataToPDFGridColumn(
  id: 'subject' | 'subjectArv' | string,
  compSequence: string[],
  data?: CompPropertyDataState['compData'] | SubjectPropertyDataState['subjectData'],
  adjustments?: CompAdjustments,
  subjectCoords?: { latitude: number; longitude: number },
  entityListingPhotos?: Blob[],
  effectiveYear?: number,
): Promise<SalesComparisonGridColumnData | null> {
  if (!data) return null;

  let {
    editableData: { propertyIdentification, improvements, transaction, site },
    readonlyData: { backupListingData },
  } = data;

  if (id === 'subjectArv') {
    improvements = (data as SubjectPropertyDataState['subjectData']).editableData.arv!.improvements;
  }

  const address = orUndefined(addressToString)(propertyIdentification?.address) ?? '';

  let propertyImage: Blob | string | undefined = entityListingPhotos?.[0];

  if (!propertyImage) {
    propertyImage = await getPropertyImageWithFallback(
      backupListingData?.listing.Media?.[0]?.MediaURL,
      propertyIdentification?.coordinates,
    );
  }

  const distanceToSubject = getDistanceToSubject(
    subjectCoords,
    propertyIdentification?.coordinates,
  );

  let arv: SalesComparisonGridColumnData['arv'] | undefined;

  if (isDefined((data as SubjectPropertyDataState['subjectData']).editableData.arv)) {
    const arvImprovements = expected(
      (data as SubjectPropertyDataState['subjectData']).editableData.arv,
    ).improvements;

    arv = {
      gla: arvImprovements.gla
        ? AloftArea.fromNumber(arvImprovements.gla.unitAmount, arvImprovements.gla.unit).toString(
            true,
          )
        : '',
      bedCount: arvImprovements.bedrooms ? arvImprovements.bedrooms.toString() : '',
      bathCount:
        arvImprovements.halfBaths || arvImprovements.fullBaths
          ? `${arvImprovements.fullBaths || 0} / ${arvImprovements.halfBaths || 0}`
          : '',
      basement: formatFeatureAreas([
        {
          area: arvImprovements.basement?.totalArea?.unitAmount,
          unit: arvImprovements.basement?.totalArea?.unit,
        },
        {
          area: arvImprovements.basement?.finishedArea?.unitAmount,
          unit: arvImprovements.basement?.finishedArea?.unit,
        },
      ]),
      quality: `Q${arvImprovements.uadQuality ?? '-'}/${arvImprovements.craftsmanQuality ?? '-'}${getFoundationDisplay(arvImprovements)}`,
      condition: `C${arvImprovements.uadCondition ?? '-'}/E${arvImprovements.effectiveAge ?? '-'}`,
    };
  }

  const yearBuilt = (effectiveYear && improvements.age) ? (effectiveYear - improvements.age).toString() : undefined;

  const totalHalfBaths = (improvements.halfBaths || improvements.basement?.halfBaths) ? ((improvements.halfBaths || 0) + (improvements.basement?.halfBaths || 0)).toString() : '0';
  const totalFullBaths = (improvements.fullBaths || improvements.basement?.fullBaths) ? ((improvements.fullBaths || 0) + (improvements.basement?.fullBaths || 0)).toString() : '0';

  return {
    imageUrl: propertyImage || '',
    address,
    dataSource:
      typeof improvements?.featureGridDataSources === 'string'
        ? improvements.featureGridDataSources
        : '',
    proximity: distanceToSubject ? `${distanceToSubject} miles` : '',
    salesPrice: transaction.status === 'SettledSale' ? formatAdjustmentPrice(transaction.price) : '',
    listingPrice: formatAdjustmentPrice(transaction.originalListPrice),
    dom: (transaction.daysOnMarket ?? '').toString(),
    contractDate: transaction.contractDate
      ? AloftDate.fromIsoString(`${transaction.contractDate}-01`).format('MM/yy')
      : '',
    saleDate: transaction.saleDate
      ? AloftDate.fromIsoString(`${transaction.saleDate}-01`).format('MM/yy')
      : '',
    saleDateAdjustment: adjustments?.dateOfSale?.adjustment?.toString(true) ?? '',
    siteSize: site.area ? AloftArea.schema.parse(site.area).toString(true) : '',
    siteInfluence: site?.influence ?? '',
    view: site?.view ?? '',
    siteSizeAdjustment: adjustments?.site?.size?.adjustment?.toString(true) ?? '',
    quality: `Q${improvements.uadQuality ?? '-'}/${improvements.craftsmanQuality ?? '-'}${getFoundationDisplay(improvements)}`,
    qualityAdjustment: adjustments?.quality?.adjustment?.toString(true) ?? '',
    condition: `C${improvements.uadCondition ?? '-'}/E${improvements.effectiveAge ?? '-'}`,
    conditionAdjustment: adjustments?.condition?.adjustment?.toString(true) ?? '',
    gla: improvements.gla
      ? AloftArea.fromNumber(improvements.gla.unitAmount, improvements.gla.unit).toString(true)
      : '',
    glaAdjustment: adjustments?.mainDwelling?.gla?.adjustment?.toString(true) ?? '',
    bedCount: improvements.bedrooms ? improvements.bedrooms.toString() : '',
    bedCountAdjustment: adjustments?.bedrooms?.adjustment?.toString(true) ?? '',
    bathCount:
      totalHalfBaths || totalFullBaths
        ? `${totalFullBaths} / ${totalHalfBaths}`
        : '',
    bathCountAdjustment: getBathCountAdjustment(adjustments),
    basement: formatFeatureAreas([
      {
        area: improvements.basement?.totalArea?.unitAmount,
        unit: improvements.basement?.totalArea?.unit,
      },
      {
        area: improvements.basement?.finishedArea?.unitAmount,
        unit: improvements.basement?.finishedArea?.unit,
      },
    ]),
    basementAdjustment: adjustments?.basement?.adjustment?.toString(true) ?? '',
    garage: getAbbreviatedCarportGarage(improvements) ?? '',
    garageAdjustment: adjustments?.garageCarport?.adjustment?.toString(true) ?? '',
    porch: formatFeatureAreas([
      {
        area: improvements.porch?.area?.unitAmount,
        unit: improvements.porch?.area?.unit,
        type: 'Porch',
      },
      {
        area: improvements.patio?.area?.unitAmount,
        unit: improvements.patio?.area?.unit,
        type: 'Patio',
      },
      {
        area: improvements.deck?.area?.unitAmount,
        unit: improvements.deck?.area?.unit,
        type: 'Deck',
      },
    ]),
    porchAdjustment: adjustments?.porchPatioDeck?.adjustment?.toString(true) ?? '',
    attic: formatFeatureArea({
      area: improvements.attic?.area?.unitAmount,
      unit: improvements.attic?.area?.unit,
    }),
    atticAdjustment: adjustments?.attic?.adjustment?.toString(true) ?? '',
    fireplace: improvements.fireplace?.count
      ? `${improvements.fireplace.count} ${improvements.fireplace.type ? improvements.fireplace.type : ''}`.trim()
      : '',
    fireplaceAdjustment: adjustments?.fireplace?.adjustment?.toString(true) ?? '',
    pool: formatFeatureAreas([
      {
        area: improvements.pool?.area?.unitAmount,
        unit: improvements.pool?.area?.unit,
        type: 'Pool',
      },
      {
        area: improvements.spa?.area?.unitAmount,
        unit: improvements.spa?.area?.unit,
        type: 'Spa',
      },
    ]),
    poolAdjustment: adjustments?.pool?.adjustment?.toString(true) ?? '',
    shed: formatFeatureArea({
      area: improvements.shed?.area?.unitAmount,
      unit: improvements.shed?.area?.unit,
    }),
    shedAdjustment: adjustments?.shed?.adjustment?.toString(true) ?? '',
    netAdjPrice: adjustments?.netAdjustment?.adjustment?.toString(true) ?? '',
    adjSalesPrice: adjustments?.adjustedSalePrice?.adjustment?.toString(true) ?? '',
    netAdj: adjustments?.netAdjustmentPct.value ?? '',
    grossAdj: adjustments?.grossAdjustmentPct.value ?? '',
    compType: id === 'subject' ? undefined : (data as CompPropertyDataState['compData'])?.compType,
    position: compSequence.indexOf(id) + 1,
    arv,
    yearBuilt,
  };
}
